import { useEffect } from 'react';
import { Input, Textarea } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { analysisQuery } from '@/entities/analysis';
import { usePersonas } from '@/entities/persona';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { AnalysisContextBanner } from '@/shared/ui';
import { PersonaSelector } from '@/shared/ui/PersonaSelector';
import { TITLE_MAX_LENGTH } from '../../constants';
import { useFocusGroupFormStore } from '../../stores/focusGroupFormStore';
import { useTranslation } from 'react-i18next';

export const SetupStep = () => {
  const { t } = useTranslation('audiences');

  const workspaceId = useCurrentWorkspaceId();
  const { data: personas = [] } = usePersonas(workspaceId);

  const title = useFocusGroupFormStore((state) => state.title);
  const topic = useFocusGroupFormStore((state) => state.topic);
  const brandName = useFocusGroupFormStore((state) => state.brandName);
  const selectedPersonaIds = useFocusGroupFormStore((state) => state.selectedPersonaIds);
  const setTitle = useFocusGroupFormStore((state) => state.setTitle);
  const setTopic = useFocusGroupFormStore((state) => state.setTopic);
  const setBrandName = useFocusGroupFormStore((state) => state.setBrandName);
  const setSelectedPersonaIds = useFocusGroupFormStore((state) => state.setSelectedPersonaIds);
  const analysisId = useFocusGroupFormStore((state) => state.analysisId);
  const prefillTitle = useFocusGroupFormStore((state) => state.prefillTitle);

  const { data: analysis } = useQuery(
    analysisQuery(analysisId ?? undefined, { suppressErrorNotification: true }),
  );
  // The analysis still travels when this cannot be read; the run records that the context did
  // not apply, which the report then states. Only the banner needs a brand to name.
  const attachedBrandName = analysisId ? analysis?.brandName : undefined;

  useEffect(() => {
    if (!attachedBrandName) return;
    const prefilled = t('focusGroup.setupStep.titleFromAnalysis', {
      brandName: attachedBrandName,
    });
    // A brand name long enough to overflow the title would make persona-api reject the run
    // before a focus group exists. The dated default is a better answer than a failed start.
    if (prefilled.length <= TITLE_MAX_LENGTH) prefillTitle(prefilled);
    if (!useFocusGroupFormStore.getState().brandName) setBrandName(attachedBrandName);
  }, [attachedBrandName, prefillTitle, setBrandName, t]);

  return (
    <div className="flex flex-col gap-12 w-full max-w-[650px]">
      <div className="flex flex-col gap-4 text-center">
        <h2 className="text-xl font-medium text-foreground">
          {t('focusGroup.setupStep.setupYourFocusGroup')}
        </h2>
        <p className="text-base text-muted-foreground">
          {t('focusGroup.setupStep.describeYourResearchGoalAnd')}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {attachedBrandName && (
          <AnalysisContextBanner
            label={t('focusGroup.setupStep.brandContextFromAnalysis', {
              brandName: attachedBrandName,
            })}
          />
        )}

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('focusGroup.setupStep.focusGroupTitle')}
          </span>
          <Input
            maxLength={TITLE_MAX_LENGTH}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('focusGroup.setupStep.brandName')}
          </span>
          <Input
            placeholder={t('focusGroup.setupStep.brandOrProductToBe')}
            value={brandName}
            onChange={(event) => setBrandName(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('focusGroup.setupStep.whatWouldYouLikeTo')}{' '}
            <span className="text-destructive">*</span>
          </span>
          <Textarea
            rows={3}
            placeholder={t('focusGroup.setupStep.enterQuestions')}
            value={topic}
            onChange={(event) => setTopic(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('focusGroup.setupStep.selectPersonasToInterview')}{' '}
            <span className="text-destructive">*</span>
          </span>
          <PersonaSelector
            value={selectedPersonaIds}
            onChange={setSelectedPersonaIds}
            personas={personas}
          />
        </div>
      </div>
    </div>
  );
};
